import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hatRecht, ERP_LESERECHTE, hatEinesDerRechte } from '../berechtigungen';
import { Icon } from './icons';
import { StatusBadge } from './ui';
import systemService from '../services/systemService';
import erpEinstellungenService from '../services/erpEinstellungenService';
import './EinrichtungsCheckliste.css';

/**
 * Macht fehlende Einrichtung sichtbar, bevor jemand in "API key not
 * configured" oder "Pflichtangaben fehlen" laeuft: KI-Anbieter, LinkedIn
 * und Lemlist aus /health/services, Firmenstamm und Kontenrahmen aus
 * /erp/einstellungen/bereitschaft.
 *
 * Rendert nichts, solange geladen wird, wenn ein Status nicht erreichbar
 * ist (fail-quiet) und wenn nichts fehlt -- ausser mit immerAnzeigen.
 *
 * Welle 2: GET /erp/einstellungen/bereitschaft erlaubt das Backend nur mit
 * einem ERP-Leserecht oder erp.einstellungen.lesen (BEREICH_AUSNAHMEN in
 * intermal/src/middleware/berechtigungen.ts). Ohne beide Rechte (Mitarbeit,
 * Marketing) laedt die Liste nur /health/services und zeigt nur KI-Anbieter,
 * LinkedIn und Lemlist -- kein 403 bei jedem Start.
 */

export type ChecklisteBereich = 'alle' | 'rechnungen';

export interface EinrichtungsChecklisteProps {
  /** 'rechnungen' zeigt nur Firmenstamm und Kontenrahmen (fuer die ERP-Seiten). */
  bereich?: ChecklisteBereich;
  /** Auch anzeigen, wenn alles eingerichtet ist (dann mit gruenen Haken). */
  immerAnzeigen?: boolean;
}

interface Punkt {
  id: string;
  label: string;
  ok: boolean;
  /** Wohin ein:e Inhaber:in zum Einrichten springt. */
  ziel: string;
  /** Was genau fehlt -- nur bei den ERP-Punkten bekannt. */
  details?: string[];
}

const KONFIGURIERT = 'configured';
const KI_ANBIETER = ['openai', 'anthropic', 'perplexity'];
/** Mangel-Text des Backends fuer den fehlenden Kontenrahmen (erp-einstellungen.service). */
const KONTENRAHMEN_MANGEL = /^Kontenrahmen/;

function punkteAusDiensten(services: Record<string, string>): Punkt[] {
  return [
    // Ein Anbieter reicht: die Generatoren fallen auf den naechsten zurueck.
    { id: 'ki', label: 'KI-Anbieter', ok: KI_ANBIETER.some((s) => services[s] === KONFIGURIERT), ziel: '/options' },
    { id: 'linkedin', label: 'LinkedIn', ok: services.linkedin === KONFIGURIERT, ziel: '/options' },
    { id: 'lemlist', label: 'Lemlist', ok: services.lemlist === KONFIGURIERT, ziel: '/options' },
  ];
}

function punkteAusBereitschaft(maengel: string[]): Punkt[] {
  const konten = maengel.filter((m) => KONTENRAHMEN_MANGEL.test(m));
  const firma = maengel.filter((m) => !KONTENRAHMEN_MANGEL.test(m));
  return [
    { id: 'firmenstamm', label: 'Firmenstamm', ok: firma.length === 0, ziel: '/erp/einstellungen', details: firma },
    // Der Kontenrahmen wird in der Buchhaltung (Reiter Konten) angelegt.
    { id: 'kontenrahmen', label: 'Kontenrahmen', ok: konten.length === 0, ziel: '/buchhaltung?tab=konten', details: konten },
  ];
}

export const EinrichtungsCheckliste: React.FC<EinrichtungsChecklisteProps> = ({ bereich = 'alle', immerAnzeigen = false }) => {
  const { user } = useAuth();
  const [punkte, setPunkte] = useState<Punkt[] | null>(null);
  const darfBereitschaft = hatEinesDerRechte(user, [...ERP_LESERECHTE, 'erp.einstellungen.lesen']);

  useEffect(() => {
    // Nur ERP-Punkte gefragt, aber kein Leserecht dafuer: nichts laden, nichts zeigen.
    if (bereich !== 'alle' && !darfBereitschaft) return undefined;
    let active = true;
    const laden = async (): Promise<Punkt[]> => {
      const [health, bereitschaft] = await Promise.all([
        bereich === 'alle' ? systemService.getHealth() : Promise.resolve(null),
        darfBereitschaft ? erpEinstellungenService.bereitschaft() : Promise.resolve(null),
      ]);
      return [
        ...(health ? punkteAusDiensten(health.services ?? {}) : []),
        ...(bereitschaft ? punkteAusBereitschaft(bereitschaft.maengel ?? []) : []),
      ];
    };
    laden()
      .then((p) => { if (active) setPunkte(p); })
      .catch(() => {
        // Status nicht erreichbar -> keine Karte (fail-quiet), die Seite bleibt nutzbar.
      });
    return () => {
      active = false;
    };
  }, [bereich, darfBereitschaft]);

  if (!punkte) return null;
  const offen = punkte.filter((p) => !p.ok);
  if (offen.length === 0 && !immerAnzeigen) return null;

  // Links fuer alle, die Optionen oder ERP-Einstellungen bearbeiten duerfen (Inhaber:in, Admin).
  const darfEinrichten = hatRecht(user, 'admin.optionen') || hatRecht(user, 'erp.einstellungen.bearbeiten');
  const titel = bereich === 'rechnungen' ? 'Bereit für Rechnungen' : 'Einrichtung';
  const hinweis = offen.length === 0
    ? 'Alles eingerichtet.'
    : darfEinrichten
      ? 'Diese Punkte fehlen noch, bevor alle Funktionen nutzbar sind.'
      : 'Bitte eine:n Inhaber:in um die Einrichtung.';

  return (
    <section className="card start-card" aria-label={titel}>
      <div className="start-card__head">
        <span className="start-card__icon"><Icon name={offen.length ? 'alert' : 'check'} size={18} /></span>
        <h2 className="card-title">{titel}</h2>
      </div>
      <p className="start-card__text">{hinweis}</p>
      <ul className="einrichtung-liste">
        {punkte.map((p) => (
          <li key={p.id} className="einrichtung-punkt">
            <StatusBadge tone={p.ok ? 'success' : 'danger'} icon={p.ok ? 'check' : 'alert'}>
              {p.ok ? 'Eingerichtet' : 'Noch nicht eingerichtet'}
            </StatusBadge>
            <span className="einrichtung-punkt__label">{p.label}</span>
            {!p.ok && darfEinrichten && (
              <Link to={p.ziel} className="einrichtung-punkt__link" aria-label={`${p.label} jetzt einrichten`}>
                Jetzt einrichten <Icon name="arrow-right" size={14} />
              </Link>
            )}
            {!p.ok && p.details && p.details.length > 0 && (
              <ul className="einrichtung-punkt__details">
                {p.details.map((d) => <li key={d}>{d}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default EinrichtungsCheckliste;
